import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AlbumCard from "@/components/AlbumCard";
import TrackRow from "@/components/TrackRow";
import { usePlayer } from "@/context/PlayerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Play, Send, Check, Zap } from "lucide-react";

const ARTIST_IMG = "https://images.unsplash.com/photo-1667266321046-5e8a744b1444?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const inputCls = "h-12 bg-transparent border-2 border-border focus:border-primary rounded-none text-white";

export default function Artist() {
  const { isAdmin } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [stats, setStats] = useState(null);
  const { playTrack } = usePlayer();

  const [form, setForm] = useState({ name: "", email: "", subject: "Booking", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadContent = () => {
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => {});
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleDelete = async (track) => {
    if (!window.confirm(`Delete "${track.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/tracks/${track.id}`);
      toast.success("Track deleted");
      loadContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div>
      {/* ARTIST HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={ARTIST_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        </div>
        <div className="relative px-6 md:px-12 py-16 md:py-24 max-w-3xl">
          <div className="overline text-xs text-accent mb-4 flex items-center gap-2"><Zap size={14} /> Artist</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight mb-4">
            FUCK THE <span className="text-primary">OPS.</span>
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-lg">
            Underground. Unfiltered. Gang gang music straight from the block. Full catalog below — for bookings, features, press and demo submissions, hit the line.
          </p>

          {/* PUBLIC STREAM COUNTER */}
          <div data-testid="artist-stats" className="flex flex-wrap gap-8 mt-8">
            <div>
              <div data-testid="artist-total-streams" className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary tabular-nums">
                {(stats?.total_streams ?? 0).toLocaleString()}
              </div>
              <div className="overline text-xs text-muted-foreground mt-1">Total Streams</div>
            </div>
            <div>
              <div className="font-display text-4xl md:text-5xl font-bold tracking-tight tabular-nums">{stats?.total_tracks ?? 0}</div>
              <div className="overline text-xs text-muted-foreground mt-1">Tracks</div>
            </div>
            <div>
              <div className="font-display text-4xl md:text-5xl font-bold tracking-tight tabular-nums">{stats?.total_albums ?? 0}</div>
              <div className="overline text-xs text-muted-foreground mt-1">Albums</div>
            </div>
          </div>

          {tracks.length > 0 && (
            <Button data-testid="artist-play" onClick={() => playTrack(tracks[0], tracks)} className="mt-6 bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline text-sm h-12 px-8">
              <Play size={18} className="mr-2" /> Play Latest
            </Button>
          )}
        </div>
      </section>

      {/* DISCOGRAPHY */}
      {albums.length > 0 && (
        <section className="px-6 md:px-12 py-10">
          <h2 className="font-display text-lg font-bold overline tracking-widest mb-6">Discography</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {albums.map((a) => <AlbumCard key={a.id} album={a} />)}
          </div>
        </section>
      )}

      {tracks.length > 0 && (
        <section className="px-6 md:px-12 py-4 pb-10">
          <h2 className="font-display text-lg font-bold overline tracking-widest mb-4">Tracks</h2>
          <div className="border-t border-border">
            {tracks.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={tracks} onDelete={isAdmin ? handleDelete : undefined} />)}
          </div>
        </section>
      )}

      {/* BOOKINGS & CONTACT */}
      <section className="px-6 md:px-12 py-12 border-t border-border">
        <div className="overline text-xs text-accent mb-2">Get In Touch</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[0.95] mb-8">
          BOOKINGS & <span className="text-primary">CONTACT.</span>
        </h2>
        <div className="max-w-xl">
          <div className="bg-card border-2 border-border p-6 md:p-8 block-shadow">
            {sent ? (
              <div data-testid="contact-sent" className="text-center py-10">
                <Check className="text-accent mx-auto mb-4" size={44} />
                <h3 className="font-display text-2xl font-bold mb-2">MESSAGE SENT.</h3>
                <p className="text-sm text-muted-foreground mb-6">We'll get back to you. No ops.</p>
                <Button data-testid="contact-again" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "Booking", message: "" }); }} variant="outline" className="rounded-none border-2 overline text-xs font-bold">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {error && <div data-testid="contact-error" className="bg-primary/10 border border-primary text-primary text-sm p-3">{error}</div>}
                <div>
                  <label className="overline text-xs text-muted-foreground mb-1 block">Name</label>
                  <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} />
                </div>
                <div>
                  <label className="overline text-xs text-muted-foreground mb-1 block">Email</label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={inputCls} />
                </div>
                <div>
                  <label className="overline text-xs text-muted-foreground mb-1 block">Subject</label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="overline text-xs text-muted-foreground mb-1 block">Message</label>
                  <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5} className="bg-transparent border-2 border-border focus:border-primary rounded-none text-white" />
                </div>
                <Button data-testid="contact-submit" type="submit" disabled={loading} className="w-full h-12 bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline">
                  <Send size={16} className="mr-2" /> {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
